import styled from "@emotion/styled"
import type { RoomBranding } from "../../../recoil/atoms/room/types"
import { isRGBAcolor, removeOpacity } from "../../../util"
import { StylesWithSelectors } from "../../../types"
export interface StylesProps {
  roomStyles?: RoomBranding
  published?: boolean
  isImage?: boolean
  hovered?: boolean
  isSmallSizePrice?: boolean
  isBadgeLiveMode?: boolean
  width?: string | number
  isTransperent?: boolean
  isMobile?: boolean
  isSmallMainScreen?: boolean
  isPiPOverlay?: boolean
  isBigBadge?: boolean
  isModerator?: boolean
  isFullSizeProductOverlay?: boolean
  isProductOverlay?: boolean
  isTransparent?: boolean
  customStyles?: StylesWithSelectors
  paddingRight?: string
}

export const Container = styled.div<StylesProps>`
  position: relative;
  flex: 1 1 0%;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  padding-left: 0.5rem;
  border-width: ${({ isTransparent }) =>
    isTransparent ? "1px" : "2px"};
  border-style: solid;
  border-color: ${({ roomStyles }) =>
    roomStyles?.badgeBorderColor ?? "#E2E8F0"};
  background: ${({ roomStyles, isTransparent }) =>
    isTransparent
      ? "rgba(0, 0, 0, 0.5)"
      : roomStyles.badgeBackgroundColor ?? "white"};
  border-radius: 0.5rem;

  &:hover {
    opacity: ${({ customStyles }) =>
      customStyles?._hoverStyles?.opacity ?? "0.8"};
  }
`

export const Stack = styled.div`
  display: flex;
`

export const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const ImageContainer = styled.div<StylesProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  margin-bottom: auto;
  align-content: center;
  hight: ${({ isSmallMainScreen }) => (isSmallMainScreen ? "50px" : "75px")};
  width: ${({ isSmallMainScreen }) => (isSmallMainScreen ? "50px" : "75px")};
`

export const InfoContainer = styled.div<StylesProps>`
  width: ${({ isImage }) => (isImage ? "75%" : "100%")};
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  opacity: ${({ hovered }) => (hovered ? "0.2" : "1")};
`

export const Title = styled.dt<StylesProps>`
  font-weight: 500;
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ roomStyles, isTransparent }) =>
    isTransparent ? "white" : roomStyles?.badgeTextColor ?? "black"};
`
export const Description = styled.dd<StylesProps>`
  opacity: 0.8;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  max-height: 85px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ roomStyles, isTransparent }) =>
    isTransparent ? "white" : roomStyles?.badgeTextColor ?? "black"};
`

export const PrimaryNumber = styled.dd<StylesProps>`
  display: flex;
  vertical-align: baseline;
  font-weight: 500;
  font-size: ${({ isSmallSizePrice }) =>
    isSmallSizePrice ? "0.75rem" : "0.875rem"};
  font-variant-numeric: proportional-nums;
  align-items: center;
  margin-right: 0.6rem;
  color: ${({ roomStyles }) => roomStyles?.badgeTextColor ?? "black"};
  word-break: break-word;
`

export const SecondaryNumber = styled.dd<StylesProps>`
  display: flex;
  vertical-align: baseline;
  font-weight: 500;
  font-size: ${({ isSmallSizePrice }) =>
    isSmallSizePrice ? "0.75rem" : "0.875rem"};
  font-variant-numeric: proportional-nums;
  align-items: center;
  margin-right: 0.2rem;
  text-decoration: line-through;
  color: ${({ roomStyles }) => roomStyles?.badgeTextColor ?? "black"};
  opacity: 0.65;
`

export const Button = styled.button<StylesProps>`
  display: inline-flex;
  height: 2rem;
  padding-left: 10px;
  padding-right: 10px;
  min-width: 50px;
  appearance: none;
  align-items: center;
  justify-content: center;
  user-select: none;
  position: relative;
  border-style: solid;
  white-space: nowrap;
  vertical-align: middle;
  outline: transparent solid 2px;
  outline-offset: 2px;
  width: auto;
  border-width: 1px;
  border-radius: 0.375rem;
  font-weight: 600;
  font-size: 0.875rem;
  line-height: 1.2;
  font-family: ${({ roomStyles }) =>
    roomStyles?.badgeButtonFontFamily
      ? roomStyles.badgeButtonFontFamily
      : "inherit"};
  background: ${({ roomStyles }) =>
    roomStyles?.badgeButtonBackgroundColor
      ? roomStyles.badgeButtonBackgroundColor
      : "#EDF2F7"};
  color: ${({ roomStyles }) =>
    roomStyles?.badgeButtonTextColor
      ? roomStyles.badgeButtonTextColor
      : "#718096"};
  border-color: ${({ roomStyles }) =>
    roomStyles?.badgeButtonBorderColor
      ? roomStyles.badgeButtonBorderColor
      : "#e2e8f0"};

  &:hover {
    background: ${({ roomStyles }) =>
      roomStyles?.badgeButtonMouseoverBackgroundColor
        ? roomStyles.badgeButtonMouseoverBackgroundColor
        : "#edf2f7"};
    color: ${({ roomStyles }) =>
      roomStyles?.badgeButtonMouseoverTextColor
        ? roomStyles.badgeButtonMouseoverTextColor
        : "#000"};
  }
`

export const OverlayContainer = styled.div<StylesProps>`
  position: relative;
  flex: 1 1 0%;
  padding-inline-start: ${({ isMobile, isBadgeLiveMode }) =>
    isMobile && isBadgeLiveMode ? "0.25rem" : "0.5rem"};
  padding-inline-end: ${({ isBadgeLiveMode, isMobile }) =>
    isMobile ? "0.25rem" : "0.5rem"};
  padding-top: ${({ isMobile }) => (isMobile ? "0.25rem" : "0.5rem")};
  padding-bottom: ${({ isMobile }) => (isMobile ? "0.25rem" : "0.5rem")};
  border-width: ${({ isFullSizeProductOverlay }) =>
    isFullSizeProductOverlay ? "0px" : "1px"};
  border-style: solid;
  border-color: ${({ roomStyles }) => roomStyles.badgeBorderColor ?? "#CBD5E0"};
  background: ${({ roomStyles, isTransperent, isProductOverlay, isBadgeLiveMode }) =>
    isProductOverlay && isRGBAcolor(roomStyles.badgeBackgroundColor) && !isBadgeLiveMode
      ? removeOpacity(roomStyles.badgeBackgroundColor)
      : isTransperent
      ? "rgba(0, 0, 0, 0.5)"
      : roomStyles.badgeBackgroundColor ?? "white"};
  border-radius: ${({ isFullSizeProductOverlay }) =>
    isFullSizeProductOverlay ? "0rem" : "0.5rem"};
  border-top-left-radius: ${({ isBadgeLiveMode }) =>
    !isBadgeLiveMode && "0px !important"};
  border-bottom-left-radius: ${({ isBadgeLiveMode }) =>
    !isBadgeLiveMode && "0px !important"};
  max-width: ${({ width }) => (width ? `${width}px` : "100%")};
  border-left: ${({ isBadgeLiveMode }) => !isBadgeLiveMode && "none"};
`
export const OverlayInfoContainer = styled.div<StylesProps>`
  display: flex;
  flex-direction: column;
  width: ${({ isBadgeLiveMode }) => isBadgeLiveMode && "100%"};
  color: ${({ isTransperent }) => isTransperent && "white"};
`

export const OverlayInfoWrapper = styled.div<StylesProps>`
  padding-left: 0.5rem;
  width: 100%;
  display: grid;
  color: ${({ roomStyles }) =>
    roomStyles?.badgeTextColor && roomStyles?.badgeTextColor};
  /* font-family: ${({ roomStyles }) =>
    roomStyles?.badgeFontFamily && roomStyles?.badgeFontFamily}; */
`

export const OverlayTitle = styled.dt<StylesProps>`
  font-size: ${({ isSmallMainScreen, isMobile, isBadgeLiveMode }) =>
    isMobile && !isBadgeLiveMode
      ? "0.875rem"
      : isMobile
      ? "1rem"
      : isSmallMainScreen
      ? "0.75rem"
      : "0.875rem"};
  font-weight: 500;
  color: ${({ isTransperent }) => isTransperent && "white"};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-left: ${({ isPiPOverlay }) => isPiPOverlay && "5px"};
  padding-right: ${({ paddingRight }) => paddingRight};
`

export const OverlayDescription = styled.dd<StylesProps>`
  opacity: 0.8;
  margin-bottom: ${({ isMobile, isBigBadge }) =>
    isBigBadge ? "0.22rem" : isMobile ? "4px" : "0.5rem"};
  font-size: ${({ isSmallMainScreen, isMobile, isBadgeLiveMode }) =>
    isMobile && !isBadgeLiveMode
      ? "0.875rem"
      : isMobile
      ? "1rem"
      : isSmallMainScreen
      ? "0.75rem"
      : "0.875rem"};
  max-height: 85px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ isTransperent }) => isTransperent && "white"};
  margin-left: ${({ isPiPOverlay }) => isPiPOverlay && "5px"};

  ul {
    display: none;
  }

  li {
    display: none;
  }
`

export const BottomContainerOverlay = styled.div`
  display: flex;
  margin-top: auto;
  -webkit-box-pack: justify;
  place-content: end space-between;
`

export const OverlayPriceWrapper = styled.div<StylesProps>`
  display: ${({ isBadgeLiveMode }) => (isBadgeLiveMode ? "flex" : "flex")};
  flex-wrap: ${({ isBadgeLiveMode }) => "wrap"};
  padding-left: 0.5rem;
  margin: auto 0px;
  width: 100%;
  overflow: hidden;
`

export const RadioButtonWrapper = styled.div<StylesProps>`
  max-width: ${({ width, isMobile }) =>
    `calc(${width}px - ${isMobile ? "1rem" : "1rem"} )`};
  overflow-y: auto;
  max-height: 74px;
`

export const ProductContainerWrapper = styled.div<StylesProps>`
  max-width: ${({ width, isMobile, isSmallMainScreen }) =>
    width
      ? `calc(${width}px - ${!isSmallMainScreen ? "75px" : "0px"} - 1rem)`
      : "100%"};
  color: ${({ roomStyles }) =>
    roomStyles?.badgeTextColor && roomStyles?.badgeTextColor};
  font-family: ${({ roomStyles }) =>
    roomStyles?.badgeFontFamily && roomStyles?.badgeFontFamily};
`

export const ProductTitle = styled.dt<StylesProps>`
  font-size: ${({ isSmallMainScreen }) =>
    isSmallMainScreen ? "0.75rem" : "1rem"};
  font-weight: 500;
  color: ${({ isTransperent }) => isTransperent && "white"};
  /*overflow: hidden; */
  /*text-overflow: ellipsis; */
  text-align: center;
  white-space: nowrap;
  color: ${({ roomStyles }) =>
    roomStyles?.badgeTextColor && roomStyles?.badgeTextColor};
  font-family: ${({ roomStyles }) =>
    roomStyles?.badgeFontFamily && roomStyles?.badgeFontFamily};
`
